import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import {
  SparklesIcon,
  PaperAirplaneIcon,
  XMarkIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';

const STORAGE_KEY = 'hiva-assistant-session';
const HIVA_SESSION_KEY = 'hiva-session-id';
const HIVA_PROXY_URL = process.env.REACT_APP_HIVA_PROXY_URL || '/api/assistant/chat';
const isAdminUser = (user) => user?.role === 'admin' || user?.role === 'super-admin';

const getRouteConfig = (pathname) => {
  if (pathname.includes('/app/leave')) {
    return {
      key: 'leave',
      label: 'Leave',
      prompts: [
        'What pending leave requests need review?',
        'Summarize leave activity for this page',
        'Explain the policy for annual leave',
        "Show the status of a specific employee's leave request"
      ]
    };
  }

  if (pathname.includes('/app/analytics')) {
    return {
      key: 'analytics',
      label: 'Analytics',
      prompts: [
        'Summarize attendance trends for this page',
        'What metrics should I check first?',
        'Explain any unusual attendance pattern'
      ]
    };
  }

  if (pathname.includes('/app/reports')) {
    return {
      key: 'reports',
      label: 'Reports',
      prompts: [
        'Summarize this report context',
        'What questions can I ask about reports?',
        'Help me interpret attendance totals'
      ]
    };
  }

  if (pathname.includes('/app/dashboard')) {
    return {
      key: 'dashboard',
      label: 'Dashboard',
      prompts: [
        'Give me a quick dashboard summary',
        'What should I review today?',
        "Explain today's attendance indicators"
      ]
    };
  }

  return {
    key: 'general',
    label: 'Staff Portal',
    prompts: [
      'Give me a quick overview',
      'What should I review today?',
      'Summarize what is on this page'
    ]
  };
};

const loadStoredMessages = () => {
  if (typeof window === 'undefined') return [];

  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];

    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    return [];
  }
};

const getStoredHivaSessionId = () => {
  if (typeof window === 'undefined') return null;
  return window.sessionStorage.getItem(HIVA_SESSION_KEY);
};

const buildWelcomeMessage = (user) => ({
  id: `welcome-${Date.now()}`,
  role: 'assistant',
  content: `Hello ${user?.firstName || 'there'}. How can I help you today?`
});

const HivaAssistant = () => {
  const location = useLocation();
  const { user } = useAuth();
  const route = getRouteConfig(location.pathname);

  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [messages, setMessages] = useState(loadStoredMessages);
  const [hivaSessionId, setHivaSessionId] = useState(getStoredHivaSessionId);
  const messagesEndRef = useRef(null);
  const canUseAssistant = isAdminUser(user);

  useEffect(() => {
    if (!canUseAssistant) return;
    if (messages.length === 0) {
      setMessages([buildWelcomeMessage(user)]);
    }
  }, [canUseAssistant, messages.length, user]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const scrollToLatestMessage = () => {
      messagesEndRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'end'
      });
    };

    const frameId = window.requestAnimationFrame(scrollToLatestMessage);
    return () => window.cancelAnimationFrame(frameId);
  }, [isOpen, messages, isSending]);

  const appendMessage = (message) => {
    setMessages((current) => [...current, message]);
  };

  const handleSend = async (presetMessage) => {
    const nextMessage = (presetMessage || input).trim();
    if (!nextMessage || isSending) return;

    appendMessage({
      id: `user-${Date.now()}`,
      role: 'user',
      content: nextMessage
    });
    setInput('');
    setIsSending(true);

    try {
      const response = await axios.post(HIVA_PROXY_URL, {
        message: nextMessage,
        route: route.key,
        sessionId: hivaSessionId,
        context: {
          label: route.label,
          path: location.pathname
        }
      });

      const nextSessionId = response.data?.data?.sessionId || null;
      if (nextSessionId) {
        setHivaSessionId(nextSessionId);
        if (typeof window !== 'undefined') {
          window.sessionStorage.setItem(HIVA_SESSION_KEY, nextSessionId);
        }
      }

      const reply =
        response.data?.data?.reply ||
        response.data?.reply ||
        'HIVA returned an empty response.';

      appendMessage({
        id: `assistant-${Date.now() + 1}`,
        role: 'assistant',
        content: reply
      });
    } catch (error) {
      appendMessage({
        id: `assistant-${Date.now() + 1}`,
        role: 'assistant',
        content: 'HIVA is temporarily unavailable. Please try again later.'
      });
    } finally {
      setIsSending(false);
    }
  };

  const clearConversation = () => {
    setMessages([buildWelcomeMessage(user)]);
    setHivaSessionId(null);
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem(HIVA_SESSION_KEY);
    }
  };

  if (!user || !canUseAssistant) return null;

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-slate-950/20 z-40 sm:hidden" onClick={() => setIsOpen(false)} />
      )}

      <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
        {isOpen && (
          <div className="w-[calc(100vw-2rem)] max-w-sm sm:w-96 h-[70vh] sm:h-[620px] bg-white border border-slate-200 shadow-2xl rounded-3xl overflow-hidden flex flex-col">
            <div className="px-5 py-4 border-b border-slate-200 bg-gradient-to-r from-slate-950 via-blue-900 to-slate-900 text-white">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2">
                    <SparklesIcon className="w-5 h-5" />
                    <h3 className="text-lg font-semibold">HIVA Assistant</h3>
                  </div>
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 rounded-lg hover:bg-white/10 transition-colors"
                  aria-label="Close HIVA assistant"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              <div className="mt-3 flex items-center justify-end gap-3">
                <button
                  onClick={clearConversation}
                  className="text-xs text-blue-100 hover:text-white transition-colors"
                >
                  Clear chat
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4 bg-slate-50 space-y-3">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] rounded-2xl px-4 py-3 shadow-sm ${
                      message.role === 'user'
                        ? 'bg-blue-600 text-white rounded-br-md'
                        : 'bg-white text-slate-800 border border-slate-200 rounded-bl-md'
                    }`}
                  >
                    <p className="text-sm leading-6">{message.content}</p>
                  </div>
                </div>
              ))}

              {isSending && (
                <div className="flex justify-start">
                  <div className="bg-white text-slate-600 border border-slate-200 rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
                    <p className="text-sm animate-pulse">HIVA is thinking...</p>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            <div className="px-4 py-3 border-t border-slate-200 bg-white">
              <div className="flex flex-wrap gap-2 mb-3">
                {route.prompts.map((prompt) => (
                  <button
                    key={prompt}
                    onClick={() => handleSend(prompt)}
                    className="text-xs px-3 py-2 rounded-full bg-slate-100 text-slate-700 hover:bg-slate-200 transition-colors"
                    disabled={isSending}
                  >
                    {prompt}
                  </button>
                ))}
              </div>

              <div className="flex items-end gap-2">
                <textarea
                  rows={2}
                  value={input}
                  onChange={(event) => setInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !event.shiftKey) {
                      event.preventDefault();
                      handleSend();
                    }
                  }}
                  placeholder="Ask HIVA..."
                  className="flex-1 resize-none rounded-2xl border border-slate-300 px-4 py-3 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  onClick={() => handleSend()}
                  disabled={isSending || !input.trim()}
                  className="h-11 w-11 rounded-2xl bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  aria-label="Send message to HIVA"
                >
                  <PaperAirplaneIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        )}

        <button
          onClick={() => setIsOpen((current) => !current)}
          className="h-14 px-5 rounded-full bg-slate-950 text-white shadow-xl hover:bg-slate-900 transition-all duration-200 flex items-center gap-3"
        >
          <div className="h-9 w-9 rounded-full bg-blue-600/90 flex items-center justify-center">
            <ChatBubbleLeftRightIcon className="w-5 h-5" />
          </div>
          <div className="text-left">
            <p className="text-sm font-semibold leading-4">HIVA</p>
            <p className="text-[11px] text-slate-300">Assistant</p>
          </div>
        </button>
      </div>
    </>
  );
};

export default HivaAssistant;
